<template>
  <v-menu
    v-model="menu"
    absolute
    offset-y
    min-width="250"
    max-height="85%"
    :close-on-content-click="false"
  >
    <!-- pass thru activator slot -->
    <template #activator="slotData">
      <slot
        name="activator"
        v-bind="slotData"
      />
    </template>

    <!-- TabItem List -->
    <v-list class="pt-2">
      <v-list-item-group>
        <!-- HOLD TABLE -->
        <template v-if="holdTabItemsVisible">
          <v-list-item
            class="mb-2"
            inactive
            @click="holdTabItemsChanged"
          >
            <v-list-item-action class="mr-3">
              <v-switch
                :input-value="holdTabItems"
                @changed="holdTabItemsChanged"
              />
            </v-list-item-action>
            <v-list-item-content>
              {{ $t('label.hold_table') }}
            </v-list-item-content>
          </v-list-item>
          <v-divider />
        </template>
        <!-- AUTOMATIC -->
        <v-list-item
          v-if="autoTabItemsVisible"
          inactive
          :disabled="autoTabItemsDisabled"
          @click="autoTabItemsChanged"
        >
          <v-list-item-action class="mr-3">
            <v-checkbox
              v-model="autoTabItems"
              :disabled="autoTabItemsDisabled"
              @click.stop="autoTabItemsChanged"
            />
          </v-list-item-action>
          <v-list-item-content>
            <v-input
              class="automatic-table-checkbox"
              :persistent-hint="tableAllocationDisabled"
              :hide-details="!tableAllocationDisabled"
              :hint="tableAllocationDisabled ? $t('label.table_allocation_disabled') : null"
            >
              <span :class="{'text--secondary': tableAllocationDisabled}">{{ $t('label.automatic') }}</span>
            </v-input>
          </v-list-item-content>
        </v-list-item>

        <v-divider v-if="autoTabItemsVisible" />

        <!-- TAB ITEMS -->
        <template v-for="(item, i) in tabItems">
          <v-list-item
            :key="i"
            inactive
            :disabled="tabItemsDisabled"
            @click.stop="toggleCheckbox(item)"
          >
            <!-- FIXME: should not have model and value at the same time-->
            <v-list-item-action class="mr-3">
              <v-checkbox
                v-model="selectedTabItems"
                :value="item"
                :disabled="tabItemsDisabled"
                :value-comparator="checkboxComparator"
                :error="isUnavailable(item)"
                @click.stop="toggleCheckbox(item)"
              />
            </v-list-item-action>
            <v-list-item-title>
              <span :class="[isUnavailable(item) ? 'error--text' : '']">
                {{ item.no }}
              </span>
              <span
                :class="[isUnavailable(item) ? 'error--text' : 'grey--text']"
                style="float:right"
              >
                {{ item.seats === 1 ?
                  $t('label.x_seat', [item.seats]) :
                  $t('label.x_seats', [item.seats])
                }}</span>
            </v-list-item-title>
          </v-list-item>
        </template>
        <v-divider />
        <v-list-item
          inactive
          :disabled="holdTabItems && holdTabItemsVisible"
          @click.stop="selectAll"
        >
          <v-list-item-action class="mr-3">
            <v-checkbox
              v-model="selectAllChecked"
              :disabled="holdTabItems && holdTabItemsVisible"
              @click.stop="selectAll"
            />
          </v-list-item-action>
          <v-list-item-title>
            <span>{{ $t('label.select_all') }}</span>
          </v-list-item-title>
        </v-list-item>
      </v-list-item-group>
    </v-list>
  </v-menu>
</template>

<script lang='ts'>
import Vue from 'vue';
import Reservation, { ReservationType } from '@/model/Reservation';
import TabItem from '@/model/TabItem';

export default Vue.extend({
  name: 'TabItemMenu',
  components: { },
  props: { },
  data() {
    return {
      menu: false,
      lastTabItems: [] as TabItem[],
      autoTabItems: false,
    };
  },
  computed: {
    r(): Reservation {
      return this.$tstore.getters.editReservation ?? new Reservation();
    },
    tabItems(): TabItem[] {
      return this.r.tab?.tabItems ?? [];
    },
    reservationTabItems(): TabItem[] {
      return this.r.tabItems ?? [];
    },
    selectedTabItems(): TabItem[] {
      return this.r.tabItems;
    },
    unavailableTabItems(): TabItem[] {
      return this.$tstore.getters.reservationUnavailableTabItems;
    },
    holdTabItems(): boolean {
      return this.r.holdTabItems;
    },
    holdTabItemsVisible(): boolean {
      return !this.r.isBlock;
    },
    selectAllChecked(): boolean {
      return this.r.tabItems.length === this.tabItems.length;
    },
    tableAllocationDisabled(): boolean {
      return !(this.$tstore.state.settings.accountSettings.tableAllocation ?? false);
    },
    autoTabItemsVisible(): boolean {
      return !this.r.isBlock;
    },
    autoTabItemsDisabled(): boolean {
      return this.holdTabItems || this.tableAllocationDisabled || this.r.isBlock;
    },
    tabItemsDisabled(): boolean {
      return this.holdTabItems && !this.r.isBlock;
    },
  },
  watch: {
    menu(value: boolean) {
      if (value) this.reset();
    },
    tableAllocationDisabled() {
      this.updateAutoTabItems();
    },
    reservationTabItems() {
      this.updateAutoTabItems();
    },
  },
  methods: {
    updateAutoTabItems() {
      this.autoTabItems = this.r.tabItems.length === 0
      && !this.tableAllocationDisabled
      && !this.r.isBlock;
    },
    selectAll() {
      if (this.selectAllChecked) {
        this.$tstore.dispatch('setReservationTabItems', []);
      } else {
        this.$tstore.dispatch('setReservationTabItems', this.tabItems);
      }
    },
    reset() {
      this.lastTabItems = [];
      this.updateAutoTabItems();
    },
    checkboxComparator(lhs: TabItem, rhs: TabItem):boolean {
      return lhs.id === rhs.id;
    },
    isUnavailable(tabItem: TabItem) {
      return this.unavailableTabItems.find((o) => o.id === tabItem.id);
    },
    toggleCheckbox(tabItem: TabItem) {
      const tabItems = Array.from(this.r.tabItems);
      const index = tabItems.findIndex((ti) => ti.id === tabItem.id);
      if (index >= 0) tabItems.splice(index, 1);
      else tabItems.push(tabItem);
      this.$tstore.dispatch('setReservationTabItems', tabItems);
    },
    holdTabItemsChanged() {
      console.log('XXX holdTabChanged');
      this.$tstore.dispatch('setReservationHoldTabItems', !this.holdTabItems);
    },
    autoTabItemsChanged() {
      if (this.autoTabItems) {
        this.lastTabItems = Array.from(this.r.tabItems);
        this.$tstore.dispatch('setReservationTabItems', []);
      } else {
        this.$tstore.dispatch('setReservationTabItems', this.lastTabItems);
      }
    },
  },
});
</script>

<style scoped>

.automatic-table-checkbox ::v-deep(.v-input__slot) {
  margin-bottom: 2px !important;
}

</style>
